import React, { useMemo, useState } from 'react';
import { DSButton, DSCheckbox, DSInput, DSModal } from '../design-system/primitives';
import './WordPressOrphanModal.css';

// WordPress hands titles back with entities still in them ("Season 38 &#8211; Week 1"),
// which is unreadable in a list the admin has to make a delete decision from.
export function decodeEntities(value) {
  if (!value) return '';

  const named = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&apos;': "'",
    '&nbsp;': ' '
  };

  return String(value)
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_, code) => String.fromCharCode(parseInt(code, 16)))
    .replace(/&[a-z0-9#]+;/gi, (entity) => named[entity.toLowerCase()] ?? entity);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// WordPress start dates arrive as local wall clock ("2026-09-15 19:00:00"). Parsed by the
// Date constructor they would drift by the UTC offset, so read the parts directly.
export function formatWhen(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(value || ''));
  if (!match) return String(value || '');

  const [, year, month, day, hour, minute] = match.map(Number);
  const date = new Date(year, month - 1, day);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return `${DAYS[date.getDay()]}, ${MONTHS[month - 1]} ${day}, ${year} at ${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

/**
 * Picks which website-only events to remove. Grouped by title, because the leftovers from
 * a deleted series all share one, and "get rid of the NEPL ones" should be a single click
 * rather than thirteen.
 */
function WordPressOrphanModal({ orphans = [], scanned = 0, submitting = false, onCancel, onRemove }) {
  const [selected, setSelected] = useState(() => new Set());
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const term = query.trim().toLowerCase();
    const byTitle = new Map();

    orphans
      .map((orphan) => ({ ...orphan, title: decodeEntities(orphan.title) }))
      .filter((orphan) => !term || orphan.title.toLowerCase().includes(term))
      .forEach((orphan) => {
        if (!byTitle.has(orphan.title)) byTitle.set(orphan.title, []);
        byTitle.get(orphan.title).push(orphan);
      });

    return Array.from(byTitle.entries())
      .map(([title, events]) => ({
        title,
        events: [...events].sort((a, b) => String(a.startDate).localeCompare(String(b.startDate)))
      }))
      .sort((a, b) => b.events.length - a.events.length || a.title.localeCompare(b.title));
  }, [orphans, query]);

  const visibleIds = useMemo(
    () => groups.flatMap((group) => group.events.map((event) => event.id)),
    [groups]
  );

  const selectedCount = selected.size;

  const toggle = (id) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setMany = (ids, shouldSelect) => {
    setSelected((current) => {
      const next = new Set(current);
      ids.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  return (
    <DSModal title="Events on the website only" size="lg" onClose={onCancel}>
      <div className="wp-orphans">
        <p className="wp-orphans__intro">
          {orphans.length} of the {scanned} events on the website have nothing behind them here.
          Anything you created by hand in WordPress also lands in this list, so pick what to remove.
        </p>

        <div className="wp-orphans__toolbar">
          <DSInput
            id="wp-orphan-search"
            placeholder="Filter by name"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
          <div className="wp-orphans__toolbar-actions">
            <DSButton
              appearance="text"
              tone="neutral"
              onClick={() => setMany(visibleIds, true)}
              disabled={visibleIds.length === 0}
            >
              Select all
            </DSButton>
            <DSButton
              appearance="text"
              tone="neutral"
              onClick={() => setSelected(new Set())}
              disabled={selectedCount === 0}
            >
              Clear
            </DSButton>
          </div>
        </div>

        <div className="wp-orphans__list">
          {groups.length === 0 && (
            <p className="wp-orphans__empty">Nothing matches "{query}".</p>
          )}

          {groups.map((group) => {
            const ids = group.events.map((event) => event.id);
            const allSelected = ids.every((id) => selected.has(id));

            return (
              <section className="wp-orphans__group" key={group.title}>
                <header className="wp-orphans__group-header">
                  <DSCheckbox
                    id={`wp-orphan-group-${group.title.replace(/\W+/g, '-')}`}
                    label={group.title}
                    checked={allSelected}
                    onChange={() => setMany(ids, !allSelected)}
                  />
                  <span className="wp-orphans__count">
                    {group.events.length} {group.events.length === 1 ? 'event' : 'events'}
                  </span>
                </header>

                <ul className="wp-orphans__dates">
                  {group.events.map((event) => (
                    <li className="wp-orphans__date" key={event.id}>
                      <DSCheckbox
                        id={`wp-orphan-${event.id}`}
                        label={formatWhen(event.startDate)}
                        checked={selected.has(event.id)}
                        onChange={() => toggle(event.id)}
                      />
                      {event.url && (
                        <a
                          className="wp-orphans__link"
                          href={event.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>

        <div className="wp-orphans__footer">
          <span className="wp-orphans__selected">
            {selectedCount} of {orphans.length} selected
          </span>
          <div className="wp-orphans__footer-actions">
            <DSButton appearance="outline" tone="neutral" onClick={onCancel}>Cancel</DSButton>
            <DSButton
              appearance="solid"
              tone="danger"
              disabled={selectedCount === 0 || submitting}
              onClick={() => onRemove(Array.from(selected))}
            >
              {submitting ? 'Removing...' : `Remove ${selectedCount} from WordPress`}
            </DSButton>
          </div>
        </div>
      </div>
    </DSModal>
  );
}

export default WordPressOrphanModal;
